import { Avatar, Tooltip } from "@mui/material";
import casesService from "api/casesService";
import tagsService from "api/tagsService";
import BinIcon from "assets/icons/delete_24.svg?react";
import SearchEntities from "components/SearchEntities";
import { IEntity } from "dto/IEntity";
import { ITag } from "dto/ITag";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { convertToLetters } from "utils/convertToLetters";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import Button from "components/Button";
import { useOnClickOutside } from "hooks/useClickOutside";
import TeamIcon from "assets/icons/team.svg?react";
import HollowButton from "components/HollowButton";
import DotsIcon from "assets/icons/three-dots.svg?react";
import { Option } from "components/OptionsCard/typings";
import { default as Dropdown2 } from "components/OptionsCard/Dropdown";
import classNames from "classnames";
import Spinner from "components/Spinner";
import InfoIcon from "assets/icons/info_icon.svg?react";
import CaseParticipantEditorModal from "components/CaseParticipantEditorModal";
import InputInline from "components/InputInline";
import NotFoundScreen from "components/NotFoundScreen";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import Popper from "components/Popper";
import { IChatParticipant } from "dto/Chat/IChat";
import { useTranslation } from "react-i18next";

const EntityRow = ({
  participant,
  onRemove,
  afterEdit,
}: {
  participant: IChatParticipant;
  onRemove?: (participant: IChatParticipant) => void;

  afterEdit: (participant: IChatParticipant) => void;
  setIsModified: (value: boolean) => void;
  allTags: ITag[];
  canEdit: boolean;
}) => {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [name] = useState<string>(participant.Label);
  const [showInfo, setShowInfo] = useState(false);
  const [editInline, setEditInline] = useState(false);
  const refWrapper = useRef();
  const refEditInput = useRef();

  useOnClickOutside(refEditInput, () => {
    if (isEditing) {
      commitName(name);
    }
  });

  // const handleEdit = () => {
  //   if (participant.ParticipantId) {
  //     setModalOptions({ ClientId: participant.ParticipantId });
  //     setModals({ ...modals, createEntity: true, hide: false });
  //     return;
  //   }
  // };

  useEffect(() => {
    const opt = [
      {
        name: t("common.delete"),
        Icon: BinIcon,
        callback: () => {
          onRemove(participant);
        },
        additional: false,
        isEnabled: true,
        canMultiple: true,
      },
    ];

    setOptions(opt);
  }, [participant]);

  const commitName = (val?: string) => {
    console.log("Setting name", val ?? name);
    participant.Label = val ?? name;
    afterEdit(participant);
    setIsEditing(false);
    setEditInline(false);
  };

  if (editInline) {
    return (
      <div className={styles.entityBox}>
        <div className={styles.entity}>
          <Avatar className={styles.avatar}>
            {convertToLetters(participant?.Label ?? "")}
          </Avatar>
          <div className={styles.grow}>
            <div className={styles.label}>
              <InputInline
                value={participant.Label}
                onCancel={() => setEditInline(false)}
                onChange={commitName}
              ></InputInline>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.entityBox}>
      <div className={styles.entity}>
        <Avatar className={styles.avatar}>
          {convertToLetters(participant?.Label ?? "")}
        </Avatar>
        <div className={styles.labelWrapper}>
          <div className={styles.label}>{participant?.Label ?? ""} </div>
          <div className={styles.icon}>
            <Tooltip title={t("participants.entityData")}>
              <div>
                <InfoIcon onClick={() => setShowInfo(!showInfo)}></InfoIcon>
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
      <div className={styles.boxes}>
        <div className={styles.more} ref={refWrapper}>
          <Tooltip title={t("common.more")}>
            <div>
              <DotsIcon
                onClick={() => {
                  setShowDropdown(!showDropdown);
                }}
                className={classNames(styles.icon)}
              />
            </div>
          </Tooltip>
          <Popper
            anchorEl={refWrapper?.current}
            open={showDropdown}
            setOpen={setShowDropdown}
          >
            <Dropdown2
              toggle={() => setShowDropdown(false)}
              options={options}
              refWrapper={refWrapper}
              rightSide={false}
              scrollWrapper={refWrapper}
              showOptions={true}
              selectedElements={[]}
            />
          </Popper>

          {showInfo && (
            <ModalLayout
              dock={DockPosition.BottomRight}
              toggle={() => setShowInfo(false)}
              refWrapper={refWrapper}
              className={styles.innerModal}
              disableMove={true}
            >
              <CaseParticipantEditorModal
                toggle={() => setShowInfo(false)}
                source={participant}
                update={() => {}}
              ></CaseParticipantEditorModal>
            </ModalLayout>
          )}
        </div>
      </div>
    </div>
  );
};

const ChatParticipantsModal: FC<Props> = ({
  toggle,
  Case,
  sourceParticipants,
  onApply,
}) => {
  const { t } = useTranslation();
  const { data: fullCase, isLoading } = casesService.useGetCase(
    Case?.Id,
    null,
    Case?.Id !== null
  );


  const [, setIsModified] = useState<boolean>(false);
  const [addNew, setAddNew] = useState<boolean>(false);
  const [participants, setParticipants] = useState<IChatParticipant[]>([]);

  const handleNewEntity = (entity: IEntity) => {
    const payload = {
      Label: entity.Label ?? entity.Name,
      UserId: entity.UserId,
      PublicKey: entity.PublicKey,
    };

    setParticipants([...participants, payload]);

    setAddNew(false);
    setIsModified(true);
  };

  const handleRemove = (participant: IChatParticipant) => {
    const index = participants.indexOf(participant, 0);

    if (index > -1) {
      const arr = participants;
      arr.splice(index, 1);
      setParticipants([...arr]);

      setIsModified(true);
    }
  };

  // const handleMove = (item: ICaseParticipant, cc: boolean) => {
  //   var normalSource = part.find((x) => x.Id === item.Id);

  //   if (normalSource) {
  //     item.Group = CaseParticipantGroup.Cc;
  //     setNormalParticipants((old) => old.filter((x) => x.Id !== item.Id));
  //   }
  // };

  const updateParticipantAfterEdit = (
    item: IChatParticipant,
    newValue?: IChatParticipant
  ) => {
    const copy = [...participants];
    const part = copy.find((x) => x.UserId === item.UserId);

    if (part) {
      const index = copy.indexOf(part);
      copy[index] = newValue;
      setParticipants(copy);
    }
  };

  useEffect(() => {
    // setParticipants(fullCase?.Participants ?? []);
    // setIsModified(false);
  }, [fullCase]);

  useEffect(() => {
    if (sourceParticipants) {
      setParticipants(sourceParticipants);
    }
    setIsModified(false);
  }, [sourceParticipants]);

  const save = () => {
    onApply(participants);
    toggle(false);
  };

  const { data: tagsData, isLoading: isLoadingTags } = tagsService.useGetTags();
  const allTags = useMemo(() => tagsData?.data ?? [], [tagsData]);

  const auth = useAuth();

  const canEdit =
    auth?.user?.profile?.role === "guest" ? Case?.Privileges === 2 : true;

  const ref = useRef(null);

  // useOnClickOutside(ref, () => {
  //   if (isModified) {
  //     setShowModal(true);
  //     return;
  //   }

  //   toggle(false);
  // });

  return (
    <div ref={ref} className={styles.wrapper}>
      <div className={styles.hdr}>
        <div className={styles.img}>
          <TeamIcon></TeamIcon>
        </div>
        <div>{t("chat.participantsList")}</div>
        {/* <CloseIcon
          onClick={() => {
            if (isModified) {
              setShowModal(true);
              return;
            }

            toggle(false);
          }}
          className={styles.closeIcon}
        /> */}
      </div>
      <div className={styles.content}>
        <SearchEntities
          removeFocus
          handleClickCreate={null}
          onChoose={(entity) => handleNewEntity(entity)}
          className={styles.searchEntities}
          disableBorder={true}
          label={t("common.search")}
          showCreateEntity={false}
          onlyUsers={true}
        />

        <div className={styles.list}>
          {(!participants || participants.length === 0) &&
            !addNew &&
            !isLoading &&
            !isLoadingTags &&
            (sourceParticipants?.length ?? 0) === 0 && (
              <div className={styles.empty}>
                <NotFoundScreen
                  title={t("participants.noEntities")}
                  text={t("participants.noEntitiesText")}
                ></NotFoundScreen>
              </div>
            )}
          {(isLoading || isLoadingTags) && (
            <div className={styles.empty}>
              <Spinner></Spinner>
            </div>
          )}

          {participants.map((participant) => {
            return (
              <EntityRow
                canEdit={canEdit}
                allTags={allTags}
                participant={participant}
                key={participant.UserId}
                onRemove={handleRemove}
                setIsModified={setIsModified}
                afterEdit={(n) => updateParticipantAfterEdit(participant, n)}
              ></EntityRow>
            );
          })}
        </div>
      </div>

      <div className={styles.btnWrapper}>
        <HollowButton
          text={t("common.cancel")}
          onClick={() => {
            toggle(false);
          }}
        ></HollowButton>
        <Button
          text={t("common.save")}
          onClick={() => {
            save();
          }}
        />
      </div>
    </div>
  );
};

export default ChatParticipantsModal;
